/**
 * FPF Wrap of studio_header_actions default_contents (L-045 / L-046).
 *
 * FPF 1.8.0 wrapComponent calls the wrapper as
 *   createElement(wrapper, { component, key, pluginProps })
 * not as a children parent. L-045 portaled `children` (always undefined),
 * so the navy host stayed empty and CSS hid the original bar — live
 * build 22 had no bell and no header search.
 *
 * Portal `component` (and `children` if a later FPF uses that) into
 * #bsg-studio-utility. Keep observing the live host: BsgHeader
 * dangerouslySetInnerHTML can replace the node after avatar fetch.
 * Markup already paints fallback bell + search; hide each fallback
 * only when a matching real control is present.
 */
import { createElement, useEffect, useState } from "react";
import { createPortal } from "react-dom";

export const HOST_ID = "bsg-studio-utility";

function findHost() {
  if (typeof document === "undefined") return null;
  return document.getElementById(HOST_ID);
}

function controlText(el) {
  return `${el.getAttribute("aria-label") || ""} ${el.getAttribute("title") || ""} ${el.className} ${el.textContent || ""}`;
}

function realControls(host) {
  const real = host?.querySelector("[data-studio-real-actions]");
  if (!real) return [];
  return [...real.querySelectorAll("button, a, [role='button']")];
}

export function markStudioUtility(host) {
  if (!host) return;
  const controls = realControls(host);
  const hasBell = controls.some((el) => /notif/i.test(controlText(el)));
  const hasSearch = controls.some((el) => /search/i.test(controlText(el)));
  host.toggleAttribute("data-has-studio-bell", hasBell);
  host.toggleAttribute("data-has-studio-search", hasSearch);
}

function findRealControl(host, pattern) {
  return realControls(host).find((el) => pattern.test(controlText(el))) || null;
}

function focusCourseSearch() {
  const field = document.querySelector(
    'input[type="search"], input[name="search"], input[placeholder*="Search" i], input[placeholder*="search" i]',
  );
  if (!field) return false;
  field.focus();
  field.scrollIntoView({ block: "center", behavior: "smooth" });
  return true;
}

/**
 * Fallback clicks: use the real tray / search overlay when FPF portaled
 * them; otherwise the search link goes to Studio home (course search).
 */
function isElement(value) {
  return Boolean(value) && typeof value === "object" && value.nodeType === 1;
}

export function bindStudioUtility(root) {
  const host =
    (isElement(root) && root.id === HOST_ID ? root : null) ||
    (isElement(root) && typeof root.querySelector === "function" ? root.querySelector(`#${HOST_ID}`) : null) ||
    findHost();
  if (!host) return () => {};

  const onClick = (event) => {
    const target = event.target;
    const trigger = isElement(target) && typeof target.closest === "function"
      ? target.closest("[data-studio-fallback]")
      : null;
    if (!trigger || !host.contains(trigger)) return;
    const kind = trigger.getAttribute("data-studio-fallback");
    if (kind === "bell") {
      const real = findRealControl(host, /notif/i);
      if (real) {
        event.preventDefault();
        real.click();
      }
      return;
    }
    if (kind === "search") {
      const real = findRealControl(host, /search/i);
      if (real) {
        event.preventDefault();
        real.click();
        return;
      }
      if (focusCourseSearch()) {
        event.preventDefault();
      }
    }
  };

  const onKey = (event) => {
    if (event.key !== "Escape") return;
    const tray = typeof document !== "undefined" ? document.getElementById("notificationTray") : null;
    const openBell = host.querySelector(".notification-button.btn-icon-light-active, .btn-icon-light-active");
    if (!tray && !openBell) return;
    const bell =
      host.querySelector("[data-testid='notification-bell-icon']") ||
      host.querySelector(".notification-button") ||
      findRealControl(host, /notif/i);
    if (!bell) return;
    event.preventDefault();
    bell.click();
    if (typeof bell.blur === "function") bell.blur();
  };

  host.addEventListener("click", onClick);
  document.addEventListener("keydown", onKey);
  return () => {
    host.removeEventListener("click", onClick);
    document.removeEventListener("keydown", onKey);
  };
}

export function StudioActionsPortal({ component, children }) {
  const [host, setHost] = useState(() => findHost());
  const body = component ?? children ?? null;

  useEffect(() => {
    const sync = () => {
      const next = findHost();
      setHost((prev) => (prev === next ? prev : next));
    };
    sync();
    if (typeof MutationObserver !== "function" || typeof document === "undefined") {
      return undefined;
    }
    const observer = new MutationObserver(sync);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!host) return undefined;
    markStudioUtility(host);
    if (typeof MutationObserver !== "function") return undefined;
    const observer = new MutationObserver(() => markStudioUtility(host));
    observer.observe(host, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [host]);

  const tree = createElement(
    "div",
    { className: "bsg-studio-real-actions", "data-studio-real-actions": "" },
    body,
  );

  if (host && typeof createPortal === "function") {
    return createPortal(tree, host);
  }
  return createElement("div", { className: "bsg-studio-actions-pending", hidden: true }, tree);
}

export default StudioActionsPortal;